'use client';

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type PlanId = 'free' | 'creator' | 'studio';

export interface SubscriptionState {
  plan: PlanId;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  currentPeriodStart: string | null;
  stripeSubscriptionId: string | null;
  stripePriceId: string | null;
}

export interface CreditState {
  balance: number;
  monthlyLimit: number;
  plan: PlanId;
  lastResetAt: string | null;
}

interface SubscriptionContextValue {
  subscription: SubscriptionState | null;
  credits: CreditState | null;
  loading: boolean;
  /** True when billing data is still syncing or could not be resolved safely. */
  syncPending: boolean;
  refreshSubscription: () => Promise<void>;
  openBillingPortal: () => Promise<void>;
  startCheckout: (planId: 'creator' | 'studio') => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue>({
  subscription: null,
  credits: null,
  loading: true,
  syncPending: false,
  refreshSubscription: async () => {},
  openBillingPortal: async () => {},
  startCheckout: async () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

export const PLAN_DETAILS = {
  free: { name: 'Free', price: '$0', credits: 5, period: 'forever' },
  creator: { name: 'Creator', price: '$12', credits: 100, period: '/month' },
  studio: { name: 'Studio', price: '$39', credits: 500, period: '/month' },
} as const;

const FREE_SUBSCRIPTION: SubscriptionState = {
  plan: 'free',
  status: 'active',
  cancelAtPeriodEnd: false,
  currentPeriodEnd: null,
  currentPeriodStart: null,
  stripeSubscriptionId: null,
  stripePriceId: null,
};

const FREE_CREDITS: CreditState = {
  balance: 5,
  monthlyLimit: 5,
  plan: 'free',
  lastResetAt: null,
};

const ACTIVE_STATUSES = ['active', 'trialing'];

function isVerifiedPaidPlan(plan: unknown): plan is Exclude<PlanId, 'free'> {
  return plan === 'creator' || plan === 'studio';
}

function isVerifiedPaidSubscription(
  plan: unknown,
  subscription:
    | {
        status?: string;
        stripeSubscriptionId?: string | null;
      }
    | null
    | undefined
): subscription is { status: string; stripeSubscriptionId: string } {
  return (
    isVerifiedPaidPlan(plan) &&
    !!subscription?.stripeSubscriptionId &&
    !!subscription?.status &&
    ACTIVE_STATUSES.includes(subscription.status)
  );
}

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionState | null>(null);
  const [credits, setCredits] = useState<CreditState | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncPending, setSyncPending] = useState(false);
  const supabase = useMemo(() => createClient(), []);
  const lastVerifiedSubscriptionRef = useRef<SubscriptionState | null>(null);
  const lastVerifiedCreditsRef = useRef<CreditState | null>(null);

  const applyCreditsFromApi = useCallback(
    (
      subData: {
        credits?: {
          balance?: number;
          monthlyLimit?: number;
          plan?: string;
          lastResetAt?: string | null;
        } | null;
        plan?: string | null;
      },
      preserveOnMissing: boolean
    ) => {
      if (subData.credits) {
        const nextCredits: CreditState = {
          balance: subData.credits.balance ?? 5,
          monthlyLimit: subData.credits.monthlyLimit ?? 5,
          plan: (subData.credits.plan ?? subData.plan ?? 'free') as PlanId,
          lastResetAt: subData.credits.lastResetAt ?? null,
        };
        setCredits(nextCredits);
        if (isVerifiedPaidPlan(nextCredits.plan)) {
          lastVerifiedCreditsRef.current = nextCredits;
        }
        return;
      }

      if (preserveOnMissing && lastVerifiedCreditsRef.current) {
        setCredits(lastVerifiedCreditsRef.current);
      }
    },
    []
  );

  const fetchSubscriptionData = useCallback(async () => {
    if (authLoading) {
      return;
    }

    if (!user) {
      setSubscription(null);
      setCredits(null);
      setSyncPending(false);
      setLoading(false);
      lastVerifiedSubscriptionRef.current = null;
      lastVerifiedCreditsRef.current = null;
      return;
    }

    setLoading(true);

    const timeout = setTimeout(() => {
      setLoading(false);
    }, 6000);

    try {
      const subResponse = await fetch('/api/stripe/subscription', {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { 'Cache-Control': 'no-cache, no-store, must-revalidate' },
      });

      clearTimeout(timeout);

      if (subResponse.ok) {
        const subData = await subResponse.json();

        if (subData.syncPending) {
          setSyncPending(true);

          if (
            isVerifiedPaidSubscription(subData.plan, {
              status: subData.subscription?.status,
              stripeSubscriptionId: subData.subscription?.stripeSubscriptionId,
            })
          ) {
            const verifiedState: SubscriptionState = {
              plan: subData.plan as PlanId,
              status: subData.subscription.status,
              cancelAtPeriodEnd: subData.subscription.cancelAtPeriodEnd ?? false,
              currentPeriodEnd: subData.subscription.currentPeriodEnd ?? null,
              currentPeriodStart: subData.subscription.currentPeriodStart ?? null,
              stripeSubscriptionId: subData.subscription.stripeSubscriptionId,
              stripePriceId: subData.subscription.stripePriceId ?? null,
            };
            setSubscription(verifiedState);
            lastVerifiedSubscriptionRef.current = verifiedState;
          } else if (lastVerifiedSubscriptionRef.current) {
            setSubscription(lastVerifiedSubscriptionRef.current);
          } else {
            setSubscription(null);
          }

          applyCreditsFromApi(subData, true);
          return;
        }

        setSyncPending(false);

        if (
          isVerifiedPaidSubscription(subData.plan, {
            status: subData.subscription?.status,
            stripeSubscriptionId: subData.subscription?.stripeSubscriptionId,
          })
        ) {
          const verifiedState: SubscriptionState = {
            plan: subData.plan as PlanId,
            status: subData.subscription.status,
            cancelAtPeriodEnd: subData.subscription.cancelAtPeriodEnd ?? false,
            currentPeriodEnd: subData.subscription.currentPeriodEnd ?? null,
            currentPeriodStart: subData.subscription.currentPeriodStart ?? null,
            stripeSubscriptionId: subData.subscription.stripeSubscriptionId,
            stripePriceId: subData.subscription.stripePriceId ?? null,
          };
          setSubscription(verifiedState);
          lastVerifiedSubscriptionRef.current = verifiedState;
        } else {
          setSubscription(FREE_SUBSCRIPTION);
          lastVerifiedSubscriptionRef.current = null;
        }

        if (subData.credits) {
          applyCreditsFromApi(subData, false);
        } else {
          const { data: credRow } = await supabase
            .from('user_credits')
            .select('balance, monthly_limit, plan, last_reset_at')
            .eq('user_id', user.id)
            .maybeSingle();

          if (credRow) {
            const nextCredits: CreditState = {
              balance: credRow.balance,
              monthlyLimit: credRow.monthly_limit,
              plan: credRow.plan as PlanId,
              lastResetAt: credRow.last_reset_at,
            };
            setCredits(nextCredits);
          } else if (lastVerifiedCreditsRef.current && lastVerifiedSubscriptionRef.current) {
            setCredits(lastVerifiedCreditsRef.current);
          } else {
            setCredits(FREE_CREDITS);
          }
        }
      } else if (subResponse.status === 403) {
        // Expected when a session exists but private-beta access is denied.
        // Do not keep stale paid-plan/credit state and do not log as an error.
        setSyncPending(false);
        setSubscription(null);
        setCredits(null);
        lastVerifiedSubscriptionRef.current = null;
        lastVerifiedCreditsRef.current = null;
      } else {
        console.error('[SubscriptionContext] Subscription API returned error:', subResponse.status);
        setSyncPending(true);
        if (lastVerifiedSubscriptionRef.current) {
          setSubscription(lastVerifiedSubscriptionRef.current);
          setCredits(lastVerifiedCreditsRef.current);
        }
      }
    } catch (err) {
      clearTimeout(timeout);
      console.error('[SubscriptionContext] Failed to fetch subscription data:', err);
      setSyncPending(true);
      if (lastVerifiedSubscriptionRef.current) {
        setSubscription(lastVerifiedSubscriptionRef.current);
        setCredits(lastVerifiedCreditsRef.current);
      }
    } finally {
      clearTimeout(timeout);
      setLoading(false);
    }
  }, [user, authLoading, supabase, applyCreditsFromApi]);

  useEffect(() => {
    fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && user) {
        fetchSubscriptionData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchSubscriptionData, user]);

  useEffect(() => {
    const handleCreditsUpdated = (event: Event) => {
      const custom = event as CustomEvent<{ balance?: number }>;
      const balance = custom.detail?.balance;
      if (typeof balance === 'number' && Number.isFinite(balance) && balance >= 0) {
        setCredits((prev) => {
          if (!prev) return prev;
          const next = { ...prev, balance };
          lastVerifiedCreditsRef.current = next;
          return next;
        });
        return;
      }
      if (user) fetchSubscriptionData();
    };
    window.addEventListener('credits-updated', handleCreditsUpdated);
    return () => window.removeEventListener('credits-updated', handleCreditsUpdated);
  }, [fetchSubscriptionData, user]);

  const refreshSubscription = useCallback(async () => {
    await fetchSubscriptionData();
  }, [fetchSubscriptionData]);

  const startCheckout = useCallback(
    async (planId: 'creator' | 'studio') => {
      try {
        const response = await fetch('/api/stripe/checkout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ planId }),
        });
        const data = await response.json();

        if (!response.ok && !data.url) {
          throw new Error(
            data.message || data.error || 'Failed to start checkout'
          );
        }

        if (data.url) {
          // upgraded:true means Stripe accepted the plan change; local plan/credits
          // activate only after the webhook — success page polls for confirmation.
          window.location.href = data.url;
        } else if (data.error === 'stripe_not_configured' || response.status === 503) {
          throw new Error(
            data.message || 'Billing is temporarily unavailable. Please contact support.'
          );
        } else {
          throw new Error(data.message || data.error || 'Failed to start checkout');
        }
      } catch (err: any) {
        console.error('[Checkout] error:', err);
        throw err;
      }
    },
    [fetchSubscriptionData]
  );

  const openBillingPortal = useCallback(async () => {
    try {
      const response = await fetch('/api/stripe/portal', { method: 'POST' });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else if (data.error === 'stripe_not_configured' || response.status === 503) {
        throw new Error(
          data.message || 'Billing is temporarily unavailable. Please contact support.'
        );
      } else if (data.error === 'no_billing_account') {
        throw new Error(
          data.message ||
            'No billing account found. Please ensure STRIPE_WEBHOOK_SECRET and SUPABASE_SERVICE_ROLE_KEY are set in your environment variables.'
        );
      } else {
        throw new Error(data.error || data.message || 'Failed to open billing portal');
      }
    } catch (err: any) {
      console.error('[Billing portal] error:', err);
      throw err;
    }
  }, []);

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        credits,
        loading,
        syncPending,
        refreshSubscription,
        openBillingPortal,
        startCheckout,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}
